/**
 * Machine ID Helpers
 * 
 * Helper functions to communicate with C++ machine ID manager via IPC
 */

import { generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../messaging/protocols/ipc';
import {
  machineIdAttestationReplyState,
  machineIdAttestationStatusState,
} from '../stores/electronStores';
import type { MachineIdAttestationStatus } from '../../coordinators/app/machineIdAttestationStatusStore';
import type { AttestationData, AttestationValidation } from './machineIdAttestation';

const STATUS_FETCH_TIMEOUT_MS = 5000;

/**
 * Request attestation data from C++ with nonce
 * Returns a promise that resolves when the reply is received
 * 
 * @param nonce - Challenge nonce from backend
 * @param clerkUserId - Clerk user ID to include in attestation payload
 */
export async function getMachineIdAttestationData(
  nonce: string,
  clerkUserId: string,
): Promise<AttestationData> {
  return new Promise((resolve, reject) => {
    // Send the request to C++ with nonce and clerkUserId
    const message = generators.system.getMachineIdAttestation(nonce, clerkUserId);
    ipcSend(message);

    // Declare listenerId variable before it's used
    let listenerId: string | null = null;

    // Set up a timeout in case we don't get a response
    const timeout = setTimeout(() => {
      if (listenerId) {
        machineIdAttestationReplyState.removeListener(listenerId);
      }
      reject(new Error('Timeout waiting for attestation data from C++'));
    }, 10000); // 10 second timeout

    // Listen for the reply from C++
    listenerId = machineIdAttestationReplyState.addListener((att) => {
      if (!att) {
        return;
      }
      // Only accept the reply for this IPC round-trip. The store is shared and replicated;
      // unrelated updates (or stale SUCCESS-shaped snapshots) must not complete this promise.
      if (att.nonceEcho !== nonce) {
        return;
      }

      clearTimeout(timeout);
      if (listenerId) {
        machineIdAttestationReplyState.removeListener(listenerId);
      }

      // Validate the response
      if (att.errorCode !== 'SUCCESS') {
        reject(new Error(`Machine ID attestation failed: ${att.error || att.errorCode}`));
        return;
      }

      if (typeof att.signature !== 'string' || att.signature.length === 0) {
        reject(new Error('No signature in attestation response'));
        return;
      }

      // Modern natives sign { machineId, clerkUserId, nonce } only. Keep the
      // C++ reply timestamp aside for legacy compatibility (old natives still
      // signed it); submitAttestation may re-attach it after local verify fails.
      const attestationPayload = {
        machineId: att.machineId,
        clerkUserId: clerkUserId,
        nonce: att.nonceEcho,
      };

      const replyTimestamp =
        typeof att.timestamp === 'number' && Number.isFinite(att.timestamp) && att.timestamp > 0
          ? att.timestamp
          : undefined;

      const attestationData: AttestationData = {
        machineId: att.machineId,
        aikPublicKey: att.aikPublicKey,
        seedSignature: att.seedSignature,
        attestationSignature: att.signature,
        attestationPayload,
        replyTimestamp,
      };

      resolve(attestationData);
    });
  });
}

/**
 * Fetch the persisted attestation status from C++ on demand by issuing a
 * cheap getMachineId IPC and waiting for the reply to land in
 * machineIdAttestationStatusState (populated by host-electron's message
 * handler). No TPM signing happens here — getMachineId is a registry read.
 *
 * Resolves with the latest status, or null on timeout.
 */
async function fetchMachineIdAttestationStatus(): Promise<MachineIdAttestationStatus | null> {
  return new Promise((resolve) => {
    let listenerId: string | null = null;
    let settled = false;

    // Single resolution path: idempotent, so a late timeout firing after a
    // successful listener (or vice versa) does not double-resolve and does
    // not double-remove the listener.
    const settle = (status: MachineIdAttestationStatus | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (listenerId) {
        machineIdAttestationStatusState.removeListener(listenerId);
      }
      resolve(status);
    };

    const timeout = setTimeout(() => {
      console.warn('[MachineIdHelpers] Timed out waiting for getMachineId reply');
      settle(null);
    }, STATUS_FETCH_TIMEOUT_MS);

    listenerId = machineIdAttestationStatusState.addListener(settle);

    // Close the race window between the caller reading currentState=null and
    // our addListener landing here: if a replication packet from main seeded
    // the store in that gap, the listener would never fire (it only catches
    // future updates) and we would time out unnecessarily. Re-check after
    // the listener is installed and consume any value already present.
    const seeded = machineIdAttestationStatusState.currentState;
    if (seeded) {
      settle(seeded);
      return;
    }

    ipcSend(generators.system.getMachineId());
  });
}

/**
 * Check if attestation is valid for the current user.
 *
 * Reads the persistence-derived attestation status that host-electron seeds
 * from the cheap systemGetMachineIdMSG reply. If the status is not yet
 * loaded (rare race with the playjector ready callback), we issue a
 * getMachineId on demand and wait for it. On timeout, fall through to
 * NOT_ATTESTED so the attestation flow runs as a safety net.
 *
 * The C++ code persists attestation metadata (attestedUserId, attestedAt,
 * attestationExpiresAt) in HKLM and exposes it via getMachineId without
 * triggering a TPM signing op.
 */
export async function checkAttestationValidity(clerkUserId: string): Promise<AttestationValidation> {
  let status = machineIdAttestationStatusState.currentState;

  if (!status) {
    status = await fetchMachineIdAttestationStatus();
  }

  if (!status) {
    return {
      isValid: false,
      reason: 'NOT_ATTESTED',
    };
  }

  // Empty / zero metadata means no successful attestation has been recorded
  // yet (fresh install or post-regenerateMachineId).
  if (!status.attestedUserId || !status.attestedAt) {
    return {
      isValid: false,
      reason: 'NOT_ATTESTED',
    };
  }

  if (status.attestationExpiresAt && status.attestationExpiresAt > 0) {
    const now = Date.now();
    if (now > status.attestationExpiresAt) {
      return {
        isValid: false,
        reason: 'EXPIRED',
        attestedUserId: status.attestedUserId,
        attestedAt: status.attestedAt,
        expiresAt: status.attestationExpiresAt,
      };
    }
  }

  if (status.attestedUserId !== clerkUserId) {
    return {
      isValid: false,
      reason: 'USER_MISMATCH',
      attestedUserId: status.attestedUserId,
      attestedAt: status.attestedAt,
      expiresAt: status.attestationExpiresAt,
    };
  }

  return {
    isValid: true,
    attestedUserId: status.attestedUserId,
    attestedAt: status.attestedAt,
    expiresAt: status.attestationExpiresAt,
  };
}

/**
 * Store attestation metadata in C++ registry after successful attestation
 * 
 * This is called after successful backend attestation to update the local state.
 * The C++ code persists this to the registry.
 */
export async function storeAttestationMetadata(
  clerkUserId: string,
  attestedAt: number,
  expiresAt?: number,
): Promise<void> {
  console.log('[MachineIdHelpers] Storing attestation metadata:', {
    clerkUserId,
    attestedAt,
    expiresAt,
  });

  // Send message to C++ to store attestation metadata
  const message = generators.system.setMachineIdAttestationMetadata(
    clerkUserId,
    attestedAt,
    expiresAt,
  );
  ipcSend(message);

  // Update the renderer-local attestation status immediately so any
  // subsequent in-process checkAttestationValidity sees the fresh state
  // without waiting for a getMachineId round-trip. Note: main may briefly
  // hold a stale value until the next playjector ready cycle; on renderer
  // reload, the on-demand fetch in checkAttestationValidity refreshes it.
  machineIdAttestationStatusState.updateState({
    attestedUserId: clerkUserId,
    attestedAt,
    attestationExpiresAt: expiresAt ?? 0,
  });

  // Note: setMachineIdAttestationMetadata is fire-and-forget.
  // The C++ code will persist this to the registry:
  // - attestedUserId: clerkUserId
  // - attestedAt: attestedAt
  // - attestationExpiresAt: expiresAt (optional)
}

